import { t } from '../../../src/services/language';
import { deleteFile, getFile, listFiles, putFile, type StoredFile } from '../idb';

/**
 * `recordings.ts`'nin web karşılığı. Masaüstünde kayıt Belgeler/TalkNote
 * klasöründe bir dosya; web'de tarayıcının kalıcı deposunda (IndexedDB) bir
 * kayıt ve "yolu" `idb:<kimlik>`. Toplantı buluta arşivlenince silinir
 * (bkz. meetingArchive.ts). Veritabanındaki `options.audioFilePath` bu biçimde
 * saklanıyor; masaüstü böyle bir yolu tanımadığı için yok sayar (ve tersi).
 */

const PREFIX = 'idb:';
const ID_PATTERN = /^idb:[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Tek bir kaydın üst sınırı — masaüstündekiyle aynı. */
const MAX_RECORDING_BYTES = 2 * 1024 * 1024 * 1024;

export function recordingsDir(): string {
  return t('Bu tarayıcı', 'This browser');
}

export function resolveRecordingPath(storedPath: string): string;
export function resolveRecordingPath(storedPath: string | undefined): string | undefined;
export function resolveRecordingPath(storedPath: string | undefined): string | undefined {
  return storedPath;
}

export function isRecordingPath(filePath: string | undefined): filePath is string {
  return typeof filePath === 'string' && ID_PATTERN.test(filePath);
}

/** Kayıt bu tarayıcıda duruyorsa yolu; yoksa (başka cihazda kaydedilmiş) null. */
export async function safeRecordingPath(filePath: string | undefined): Promise<string | null> {
  if (!isRecordingPath(filePath)) return null;
  return (await getFile(fileId(filePath)).catch(() => null)) ? filePath : null;
}

export function fileId(filePath: string): string {
  return filePath.slice(PREFIX.length);
}

export function filePathFor(id: string): string {
  return `${PREFIX}${id}`;
}

export async function readRecording(filePath: string): Promise<StoredFile> {
  if (!isRecordingPath(filePath)) throw new Error(t('Bu dosyaya erişilemez.', 'This file cannot be accessed.'));
  const file = await getFile(fileId(filePath));
  if (!file) throw new Error(t('Kayıt bu tarayıcıda bulunamadı.', 'The recording was not found in this browser.'));
  return file;
}

export async function removeRecording(filePath: string): Promise<void> {
  if (!isRecordingPath(filePath)) return;
  await deleteFile(fileId(filePath));
  const url = objectUrls.get(filePath);
  if (url) URL.revokeObjectURL(url);
  objectUrls.delete(filePath);
}

/**
 * Arşivlenmemiş kayıtların oynatma adresleri (`blob:`). Masaüstünde yerel
 * dosya file:// ile çalınıyor; burada tarayıcı deposundaki dosyanın adresi.
 * Eşzamanlı sorulduğu için (renderer > lib/fileUrl.ts) önceden dolduruluyor.
 */
const objectUrls = new Map<string, string>();

export function localAudioUrl(filePath: string): string | undefined {
  return objectUrls.get(filePath);
}

/** Açılışta bu tarayıcıdaki kayıtların adreslerini hazırlar. */
export async function primeLocalAudioUrls(): Promise<void> {
  for (const file of await listFiles().catch(() => [])) {
    const path = filePathFor(file.id);
    if (!objectUrls.has(path)) objectUrls.set(path, URL.createObjectURL(file.blob));
  }
}

export async function storeBlob(blob: Blob, name: string, kind: StoredFile['kind']): Promise<string> {
  if (blob.size === 0) throw new Error(t('Kaydedilecek ses verisi boş.', 'There is no audio data to save.'));
  if (blob.size > MAX_RECORDING_BYTES) throw new Error(t('Kayıt çok büyük.', 'The recording is too large.'));
  const id = crypto.randomUUID();
  try {
    await putFile({ id, blob, name, kind, createdAt: Date.now() });
    objectUrls.set(filePathFor(id), URL.createObjectURL(blob));
  } catch (err) {
    console.error('[recordings] tarayıcıya yazılamadı:', err);
    throw new Error(
      t(
        'Kayıt tarayıcıya kaydedilemedi (depolama alanı dolu ya da gizli pencere olabilir).',
        'The recording could not be saved in this browser (storage may be full or this is a private window).'
      )
    );
  }
  return filePathFor(id);
}

/** Tarayıcının kaydı webm; mobilde telefonun kaydı AAC (mobile/src/shims/nativeMic.ts). */
export async function saveRecording(buffer: ArrayBuffer, mimeType = 'audio/webm'): Promise<string> {
  const extension = mimeType === 'audio/aac' ? 'aac' : 'webm';
  const name = `talknote-kayit-${new Date().toISOString().replace(/[:.]/g, '-')}.${extension}`;
  return storeBlob(new Blob([buffer], { type: mimeType }), name, 'recording');
}

/** Masaüstünde "klasörde göster"; web'de dosyayı indirir. */
export async function revealRecording(filePath: string): Promise<void> {
  const file = await readRecording(filePath);
  const url = URL.createObjectURL(file.blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = file.name;
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}
